import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { SiReact, SiNodedotjs, SiMongodb, SiDocker } from 'react-icons/si';
import { FaAmazon } from 'react-icons/fa6';
import HeroServiceGrid from './HeroServiceGrid';

const Ember = ({ bottom, left, right, duration, delay }) => (
  <motion.div
    className="absolute rounded-full bg-orange-500 pointer-events-none"
    style={{ width: '5px', height: '5px', bottom, left, right }}
    animate={{ y: [0, -120, -140], x: [0, 18, 28], opacity: [0.85, 0.5, 0], scale: [1, 0.7, 0.3] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'easeOut' }}
  />
);

const embers = [
  { bottom: '15%', left: '4%',   duration: 3.3, delay: 0    },
  { bottom: '8%',  left: '12%',  duration: 4.2, delay: 0.75 },
  { bottom: '22%', left: '22%',  duration: 2.9, delay: 1.4  },
  { bottom: '6%',  left: '38%',  duration: 3.7, delay: 0.3  },
  { bottom: '14%', right: '34%', duration: 3.1, delay: 1.0  },
  { bottom: '20%', right: '18%', duration: 4.0, delay: 0.55 },
  { bottom: '10%', right: '8%',  duration: 3.4, delay: 1.7  },
];

const HeroSection = ({ onServiceClick }) => {
  const linesRef = useRef(null);

  useEffect(() => {
    const counters = document.querySelectorAll('.counter');
    const speed = 200;
    const animateCounter = (counter) => {
      const target = +counter.getAttribute('data-target');
      const increment = target / speed;
      const update = () => {
        const count = +counter.innerText.replace('+', '').replace('%', '');
        if (count < target) {
          counter.innerText = Math.ceil(count + increment) + '%';
          setTimeout(update, 1);
        } else {
          counter.innerText = target + '%';
        }
      };
      update();
    };
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { animateCounter(e.target); obs.unobserve(e.target); } }),
      { threshold: 0.5 }
    );
    counters.forEach(c => obs.observe(c));
  }, []);

  useEffect(() => {
    if (!linesRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { linesRef.current.classList.add('forge-lines-visible'); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    obs.observe(linesRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="hero"
      className="min-h-screen flex flex-col justify-center relative overflow-hidden bg-gray-950 pt-16 sm:pt-20"
    >
      {embers.map((e, i) => <Ember key={i} {...e} />)}

      <div
        className="pointer-events-none absolute bottom-0 left-0 w-96 h-96 opacity-15"
        style={{ background: 'radial-gradient(circle, #f97316 0%, transparent 65%)' }}
      />

      <div className="container mx-auto px-6 relative z-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT */}
          <div>
            <motion.div
              className="inline-flex items-center border border-orange-900/50 bg-orange-950/25 px-3 sm:px-5 py-2 mb-6 sm:mb-10 text-orange-400 text-xs font-black uppercase tracking-widest"
              style={{ borderRadius: '2px' }}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              ⚡ Scope First. Build Fast.
            </motion.div>

            <p className="text-gray-600 text-xs italic tracking-wide mb-4 sm:mb-6">
              Old trust, better tools.
            </p>

            <div ref={linesRef} className="mb-8 leading-none select-none">
              <span className="forge-line forge-line-1 text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-black text-white tracking-tight block">
                <span className="forge-line-inner">SCOPE.</span>
              </span>
              <span
                className="forge-line forge-line-2 text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-black tracking-tight block"
                style={{ color: 'transparent', WebkitTextStroke: '2px rgba(255,255,255,0.18)' }}
              >
                <span className="forge-line-inner">BUILD.</span>
              </span>
              <span className="forge-line forge-line-3 text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-black text-orange-500 tracking-tight block">
                <span className="forge-line-inner">LAUNCH.</span>
              </span>
            </div>

            <motion.p
              className="text-base text-gray-400 max-w-sm leading-relaxed mb-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.55 }}
            >
              We map your entire project with AI before you spend a coin, then build it fast.{' '}
              <span className="text-orange-400 font-semibold">No guesswork. No surprise invoices.</span>
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-10"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.65 }}
            >
              <motion.button
                className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 font-black text-sm uppercase tracking-wide flex items-center gap-2 transition-colors duration-200"
                style={{ borderRadius: '2px' }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => document.getElementById('analysis') && document.getElementById('analysis').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Your Website Analyzed <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                className="border-2 border-gray-700 hover:border-orange-500 text-gray-300 hover:text-orange-400 px-7 py-3.5 font-black text-sm uppercase tracking-wide flex items-center gap-2 transition-colors duration-200"
                style={{ borderRadius: '2px' }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => document.getElementById('contact') && document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                <MessageCircle className="w-4 h-4" /> Talk to Us
              </motion.button>
            </motion.div>

            <div className="forge-stat-strip border-t border-b border-gray-800 grid grid-cols-3 divide-x divide-gray-800 mb-8">
              <div className="py-5 pr-4 text-center">
                <div className="text-2xl font-black text-orange-500 mb-0.5">5+</div>
                <div className="text-[10px] text-gray-600 uppercase tracking-widest font-bold">Years</div>
              </div>
              <div className="py-5 px-4 text-center">
                <div className="text-2xl font-black text-white mb-0.5 counter" data-target="100">0</div>
                <div className="text-[10px] text-gray-600 uppercase tracking-widest font-bold">Guarantee</div>
              </div>
              <div className="py-5 pl-4 text-center">
                <div className="text-2xl font-black text-orange-500 mb-0.5">24/7</div>
                <div className="text-[10px] text-gray-600 uppercase tracking-widest font-bold">Support</div>
              </div>
            </div>

            <motion.div
              className="flex items-center gap-5 overflow-hidden mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <span className="text-[10px] text-gray-700 uppercase tracking-widest font-black whitespace-nowrap shrink-0">
                Trusted by
              </span>
              <div className="overflow-hidden flex-1">
                <div className="forge-ticker">
                  {['Tronomy', 'Kimisitu Investments', 'GeoMineGlobal',
                    'Tronomy', 'Kimisitu Investments', 'GeoMineGlobal'].map((name, i) => (
                    <span key={i} className="text-sm font-bold text-gray-500 inline-block">{name}</span>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              className="pt-6 border-t border-gray-800/40 flex flex-wrap items-center gap-7"
              style={{ opacity: 0.25 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.25 }}
              transition={{ duration: 0.7, delay: 1.1 }}
            >
              <SiReact className="w-6 h-6 text-white" />
              <FaAmazon className="w-6 h-6 text-white" />
              <SiNodedotjs className="w-6 h-6 text-white" />
              <SiMongodb className="w-6 h-6 text-white" />
              <SiDocker className="w-6 h-6 text-white" />
            </motion.div>
          </div>

          {/* RIGHT — animated service grid */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <HeroServiceGrid onServiceClick={onServiceClick} />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
